/**
 * Labels and badge colours for audits and their actions (Audit Lite).
 * Audits read "Completed", never "Approved": the verdict is the outcome.
 */
import type { AuditActionPriority, AuditActionStatus, AuditOutcome, AuditStatus } from '../../lib/api';

export const AUDIT_STATUS: Record<AuditStatus, { label: string; className: string }> = {
  in_progress: { label: 'In progress', className: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200' },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200' },
  expired: { label: 'Expired', className: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200' },
};

/** Badge for an archived audit (shown beside its status). */
export const AUDIT_ARCHIVED = {
  label: 'Archived', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
} as const;

export const AUDIT_OUTCOME: Record<Exclude<AuditOutcome, ''>, { label: string; className: string }> = {
  pass: { label: 'Pass', className: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200' },
  pass_with_actions: { label: 'Pass with actions', className: 'bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-200' },
  reaudit: { label: 'Re-audit required', className: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200' },
  fail: { label: 'Fail', className: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200' },
};

export const OUTCOME_OPTIONS = (Object.keys(AUDIT_OUTCOME) as Array<Exclude<AuditOutcome, ''>>)
  .map((value) => ({ value, label: AUDIT_OUTCOME[value].label }));

export const ACTION_STATUS: Record<AuditActionStatus, { label: string; className: string }> = {
  open: { label: 'Open', className: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200' },
  in_progress: { label: 'In progress', className: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200' },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' },
};

export const PRIORITY_LABEL: Record<AuditActionPriority, string> = {
  low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical',
};

/** Whole days between today and `iso` (negative = past). */
export function daysFromToday(iso: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return Math.round((day.getTime() - today.getTime()) / 86_400_000);
}

/** ISO date (YYYY-MM-DD) `days` from today — default due date for a new action. */
export function isoDateFromToday(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * What an audit is called: its own name, else its template's name.
 *
 * @param audit - any audit row (`name` may be '' or missing on old payloads)
 * @returns the display name
 */
export function auditTitle(audit: { name?: string; template_name: string }): string {
  return audit.name?.trim() || audit.template_name;
}

/** The template's name up to its first dash ("Supplier Audit Sheet – Garden …" → "Supplier Audit Sheet"). */
export function shortTemplateName(name: string): string {
  const head = (name || '').trim().split(/\s+[–—-]\s+/)[0] ?? '';
  return (head.slice(0, 60).trim() || (name || '').slice(0, 60));
}

/**
 * The default audit name, as the server builds it: "<supplier> – <template> – 09 Oct 2026".
 * The server adds " (2)" when that name is already used.
 */
export function defaultAuditName(supplierName: string, templateName: string, date: Date = new Date()): string {
  const day = `${String(date.getDate()).padStart(2, '0')} ${date.toLocaleString('en-GB', { month: 'short' })} ${date.getFullYear()}`;
  return `${supplierName.trim().slice(0, 80)} – ${shortTemplateName(templateName)} – ${day}`;
}
